import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { graphql, compose } from 'react-apollo';
import find from 'lodash/find';
import gql from 'graphql-tag';

import WBS from './WBS.js';
import Loading from '../../../components/Loading.js';

const queryElements = gql`
  query Elements($typeId: String, $projectId: String) {
    elements(typeId: $typeId, projectId: $projectId) {
      id
      name
      parent_id
      order
    }
  }
`;

const WBS_ELEMENT_CREATED_SUBSCRIPTION = gql`
  subscription newElementsCreated($typeId: String!, $projectId: String) {
    newElementsCreated(typeId: $typeId, projectId: $projectId) {
      id
      name
      parent_id
      order
    }
  }
`;

const WBS_ELEMENT_DELETED_SUBSCRIPTION = gql`
  subscription deleteElement($typeId: String!, $projectId: String!) {
    deleteElement(typeId: $typeId, projectId: $projectId) {
      id
      name
      parent_id
      order
    }
  }
`;

const WBS_ELEMENT_CHANGED_SUBSCRIPTION = gql`
  subscription elementChanged($typeId: String!, $projectId: String!) {
    elementChanged(typeId: $typeId, projectId: $projectId) {
      id
      name
      parent_id
      order
    }
  }
`;

const changeParentOrOrderMutation = gql`
  mutation changeParentOrOrder(
    $id: String
    $newParentId: String
    $order: String
    $projectId: String
  ) {
    changeParentOrOrder(
      id: $id
      newParentId: $newParentId
      order: $order
      projectId: $projectId
    ) {
      id
      name
      parent_id
      order
    }
  }
`;
const withData = compose(
  graphql(queryElements, {
    options: ({ wbsTypeId, currentProjectId }) => ({
      variables: {
        typeId: wbsTypeId,
        projectId: currentProjectId,
      },
      fetchPolicy: 'network-only',
    }),
    props: props => {
      return {
        subscribeToNewWbsElements: currentProps => {
          return props.data.subscribeToMore({
            document: WBS_ELEMENT_CREATED_SUBSCRIPTION,
            variables: {
              typeId: currentProps.wbsTypeId,
              projectId: currentProps.currentProjectId,
            },
            updateQuery: (prev, { subscriptionData }) => {
              if (
                !subscriptionData.data ||
                !subscriptionData.data.newElementsCreated ||
                !subscriptionData.data.newElementsCreated.length
              ) {
                return prev;
              }

              const newWbsElements = subscriptionData.data.newElementsCreated;
              return {
                ...prev,
                elements: prev.elements.concat(newWbsElements),
              };
            },
          });
        },
        subscribeToWbsElementDelete: currentProps => {
          return props.data.subscribeToMore({
            document: WBS_ELEMENT_DELETED_SUBSCRIPTION,
            variables: {
              typeId: currentProps.wbsTypeId,
              projectId: currentProps.currentProjectId,
            },
            updateQuery: (prev, { subscriptionData }) => {
              if (
                !subscriptionData.data ||
                !subscriptionData.data.deleteElement
              ) {
                return prev;
              }

              const deletedElement = subscriptionData.data.deleteElement;
              const deletedElementOrder = parseInt(deletedElement.order, 10);

              const newElementArray = prev.elements.reduce((previous, cur) => {
                const newCur = Object.assign({}, cur);
                const eleOrder = parseInt(newCur.order, 10);
                if (
                  newCur.parent_id === deletedElement.parent_id &&
                  eleOrder > deletedElementOrder
                ) {
                  newCur.order = (eleOrder - 1).toString();
                  previous.push(newCur);
                  return previous;
                }
                if (newCur.id !== deletedElement.id) {
                  previous.push(newCur);
                  return previous;
                }
                return previous;
              }, []);

              return {
                ...prev,
                elements: newElementArray,
              };
            },
          });
        },
        subscribeToWbsElementChange: currentProps => {
          return props.data.subscribeToMore({
            document: WBS_ELEMENT_CHANGED_SUBSCRIPTION,
            variables: {
              typeId: currentProps.wbsTypeId,
              projectId: currentProps.currentProjectId,
            },
            updateQuery: (prev, { subscriptionData }) => {
              if (
                !subscriptionData.data ||
                !subscriptionData.data.elementChanged
              ) {
                return prev;
              }

              const changedElement = subscriptionData.data.elementChanged;

              const x = prev.elements.map(ele => {
                if (ele.id === changedElement.id) {
                  return changedElement;
                }
                return ele;
              });

              return {
                ...prev,
                elements: x,
              };
            },
          });
        },
        ...props,
      };
    },
  }),
  graphql(changeParentOrOrderMutation, {
    props: props => ({
      changeParentOrOrderMutation: (
        id,
        newParentId,
        order,
        projectId,
      ) => {
        props.mutate({
          variables: { id, newParentId, order, projectId },
          // update: (proxy, { data: { createComment } }) => {
          //   const data = proxy.readQuery({
          //     queryElements,
          //     variables: {
          //       typeId: props.wbsTypeId,
          //       projectId: props.currentProjectId,
          //     },
          //   });

          //   proxy.writeQuery({
          //     queryElements,
          //     variables: {
          //       typeId: props.wbsTypeId,
          //       projectId: props.currentProjectId,
          //     },
          //     data,
          //   });
          // },
          updateQueries: {
            Elements: (prev, { mutationResult }) => {
              const newElements = mutationResult.data.changeParentOrOrder;
              const currentPrev = [...prev.elements];

              const prevElement = find(prev.elements, a => {
                return a.id === newElements.id;
              });

              const oldParent = prevElement.parent_id;
              const oldOrder = prevElement.order;
              const newOrder = newElements.order;
              const newParent = newElements.parent_id;

              const newElementsArray = currentPrev.map(ele => {
                const currentEleOrder = parseInt(ele.order, 10);
                if (ele.id === newElements.id) {
                  ele.order = newElements.order.toString();
                  ele.parent_id = newElements.parent_id;
                  return ele;
                }
                if (ele.parent_id === oldParent && currentEleOrder > oldOrder) {
                  ele.order = (currentEleOrder - 1).toString();
                  return ele;
                }
                if (
                  ele.parent_id === newParent &&
                  currentEleOrder >= newOrder
                ) {
                  ele.order = (currentEleOrder + 1).toString();
                  return ele;
                }
                return ele;
              });

              return {
                ...prev,
                elements: newElementsArray,
              };
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            changeParentOrOrder: {
              id: id,
              name: 'temp',
              parent_id: newParentId,
              order: order,
              created_at: new Date().toString(),
              __typename: 'Element',
            },
          },
        });
      },
    }),
  }),
);

@withData
export default class WBSContainer extends Component {
  static propTypes = {};

  componentWillMount() {
    const {
      subscribeToNewWbsElements,
      subscribeToWbsElementChange,
      subscribeToWbsElementDelete,
    } = this.props;

    console.log("this: ", this.subscription)
    this.subscription.subscribeToNewWbsElements = subscribeToNewWbsElements(
      this.props,
    );
    this.subscription.subscribeToWbsElementDelete = subscribeToWbsElementDelete(
      this.props,
    );
    this.subscription.subscribeToWbsElementChange = subscribeToWbsElementChange(
      this.props,
    );
  }

  componentWillUnmount() {
    this.subscription.subscribeToNewWbsElements();
    this.subscription.subscribeToWbsElementDelete();
    this.subscription.subscribeToWbsElementChange();
  }

  subscription = {
    subscribeToNewWbsElements: null,
    subscribeToWbsElementChange: null,
    subscribeToWbsElementDelete: null,
  };

  render() {
    const { data: { loading, elements } } = this.props;
    if (loading) return <Loading loadingMessage="WBS Elements are Loading..." />;
    return (
      <div
        style={{ display: 'flex', flex: 1, flexDirection: 'row', padding: 10 }}
      >
        <WBS elements={elements} {...this.props} />
      </div>
    );
  }
}

WBSContainer.propTypes = {
  data: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/views/Project/WBS/WBSContainer.js